import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { translate } from '../../../services/translate';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import { dashboardSummaryService, PlateGroup } from '../../../services/central-api/dashboard-summary';
import useFilters from '../../../components/Filter/hooks/useFilters';
import MyDataTable from '../../../components/MyDataTable';
import { Pagination } from '../../../components/Pagination';
import PlateGroupModal from './PlateGroupModal';
import moment from 'moment';
import DropdownButton from '../../../components/DropdownButton';
import DeletePlateGroupModal from './DeletePlateGroupModal';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';

interface Props {
    showCreateModal: boolean;
    setShowCreateModal: Dispatch<SetStateAction<boolean>>;
}

export default function PlateGroupTable({ setShowCreateModal, showCreateModal }: Props) {
    const { styles } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const [totalItems, setTotalItems] = useState(0);
    const [plateGroups, setPlateGroups] = useState<PlateGroup[]>([]);

    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteIsModalVisible] = useState<boolean>(false);
    const [selectedPlateGroup, setSelectedPlateGroup] = useState<PlateGroup | undefined>();

    const [filters, setFilters] = useFilters<{ textFilter: string | undefined; page: number; limit: number; }>({
        textFilter: undefined,
        page: 0,
        limit: 25,
    });

    const queryResult = useQuery({
        queryKey: ['plateGroupModal', filters],
        queryFn: () => dashboardSummaryService.getPlateGroups(filters),
        enabled: !!authenticatedUser,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setPlateGroups(data.rows);
        setTotalItems(data.count);
    }, [data]);

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            refetch();
        }
    }, [isFocused, filters]));

    return (
        <>
            <View style={styles.flex}>
                <View style={styles.cardList}>
                    <MyDataTable
                        columns={[
                            {
                                name: translate('name'),
                                cell: row => row.name,
                                grow: 1,
                            },
                            {
                                name: translate('description'),
                                cell: row => row.description,
                                grow: 1,
                            },
                            {
                                name: translate('activePlates'),
                                cell: row => row.activePlates,
                                grow: 1,
                            },
                            {
                                name: translate('inactivePlates'),
                                cell: row => row.inactivePlates,
                                grow: 1,
                            },
                            {
                                name: translate('registeredOn'),
                                cell: row => moment(row.createdAt).format('DD/MM/YYYY'),
                                grow: 1,
                            },
                            {
                                name: '',
                                wrap: true,
                                button: true,
                                cell: (row) => {
                                    return <DropdownButton
                                        actions={[
                                            {
                                                content: translate('editInformation'),
                                                onPress: () => {
                                                    setSelectedPlateGroup(row);
                                                    setIsModalVisible(true);
                                                }
                                            },
                                            {
                                                content: translate('delete'),
                                                variant: 'danger',
                                                onPress: () => {
                                                    setSelectedPlateGroup(row);
                                                    setIsDeleteIsModalVisible(true);
                                                }
                                            }
                                        ]}
                                    />;
                                }
                            }
                        ]}
                        data={plateGroups}
                        progressPending={isLoading}
                    />
                </View>
                <Pagination
                    currentPage={filters.page}
                    totalItems={totalItems}
                    setPage={page => {
                        setFilters(old => ({ ...old, page }));
                    }}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({ ...old, limit: pageSize, page: 0 }));
                    }}
                />
            </View>
            <PlateGroupModal
                isModalVisible={isModalVisible || showCreateModal}
                setModalVisible={(val) => {
                    if (showCreateModal) {
                        setShowCreateModal(val);
                    } else {
                        setIsModalVisible(val);
                    }
                }}
                onConfirm={refetch}
                onClose={() => {
                    setSelectedPlateGroup(undefined);
                }}
                selectedPlateGroupId={selectedPlateGroup?.id}
            />
            {selectedPlateGroup && <DeletePlateGroupModal
                isModalVisible={isDeleteModalVisible}
                setModalVisible={setIsDeleteIsModalVisible}
                onConfirm={refetch}
                onClose={() => {
                    setSelectedPlateGroup(undefined);
                }}
                selectedPlateGroup={selectedPlateGroup}
            />}
        </>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    cardList: {
        borderWidth: 1,
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        borderColor: theme.colors.dataTable.border,
        backgroundColor: theme.colors.dataTable.background,
        flex: 1,
    },
    flex: {
        flex: 1,
    },
}));
